<template>
  <div class="flex items-center gap-2 c-component-list-item relative">
    <slot name="prefix">
      <CIcon
        v-if="reorderable"
        class="pr-2 cursor-grabbing c-component-list-item__drag-handle flex-grow-0 flex-shrink-0"
        :color="themeVars.primary3"
        icon="grip-dots-vertical"
        size="20"
      />
    </slot>
    <CCard
      :bordered="isClicked"
      class="overflow-hidden"
      :class="{
        'cursor-pointer': clickable,
      }"
      size="medium"
      :style="{ border: `1px solid ${isClicked ? themeVars.primary : 'transparent'}!important` }"
      :theme-overrides="
        !description
          ? {
              paddingMedium: '8px 16px',
            }
          : {}
      "
      @click="clickable && emit('selectComponent', component!)"
    >
      <div class="flex flex-col min-h-8">
        <div class="flex flex-grow-1 items-center" :class="[description ? 'gap-4' : 'gap-2']">
          <CIcon v-if="isHome" class="flex-grow-0 flex-shrink-0" :color="themeVars.primary" icon="home" size="16" />
          <CIcon
            v-else
            class="flex-grow-0 flex-shrink-0"
            :color="themeVars.primary4"
            :fa-type="icon.type"
            :icon="icon.name"
            :size="description ? 24 : 16"
          />
          <div class="overflow-hidden">
            <div
              v-tooltip="displayTitle"
              class="title font-500 text-truncate"
              :style="{ fontSize: description ? '18px' : '14px' }"
            >
              {{ displayTitle }}
            </div>

            <div v-if="description" class="description">
              {{ description }}
            </div>
          </div>

          <CButton
            v-if="hasChildren"
            circle
            class="flex-grow-0 flex-shrink-0 ml-auto"
            size="small"
            @click="emit('displayChildren', component!)"
          >
            <CIcon icon="angle-right" :size="12" />
          </CButton>
        </div>
      </div>
    </CCard>

    <div class="flex items-center gap-2 flex-grow-0 flex-shrink-0 ml-auto">
      <CDropdown
        v-if="showComponentMenu"
        :options="menuOptions"
        placement="bottom-end"
        trigger="click"
        @select="handleMenuSelect"
      >
        <CButton class="!p-1" text @click.stop>
          <CIcon :color="themeVars.primary" icon="ellipsis" :size="16" />
        </CButton>
      </CDropdown>
    </div>
    <CButton v-if="isRemovable && !showComponentMenu" text @click="emit('removeComponent', component!)">
      <template #icon>
        <CIcon class="color-error" icon="trash" size="16" />
      </template>
    </CButton>

    <CRenameModal
      v-if="showRenameModal"
      v-model="renameForm"
      @cancel="showRenameModal = false"
      @confirm="onRenameSubmit"
    />
  </div>
</template>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

import CCard from '@lib/components/CCard/CCard.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CRenameModal from '@lib/components/CRenameModal/CRenameModal.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import type { ComponentNode } from '@canvas-builder/types/canvas'
import { ComponentIcon, componentIconType, ComponentTypes } from '@canvas-builder/types/canvas'
import { getComponentTitle, getNodeDisplayNameByComponentType } from '@canvas-builder/util/canvas.util'
import type { FontAwesomeType } from '@canvas-builder/types/font-awesome.types'
import { computed, h, ref } from 'vue'
import type { DropdownMixedOption } from 'naive-ui/lib/dropdown/src/interface'

interface Props {
  icon?: { name: string; type?: FontAwesomeType }
  type: ComponentTypes
  reorderable?: boolean
  description?: string
  title?: string
  component?: ComponentNode
  removable?: boolean
  clickable?: boolean
  isClicked?: boolean
  isHome?: boolean
  showComponentManager?: boolean
}

const { t } = useI18n()
const props = defineProps<Props>()
const emit = defineEmits<{
  displayChildren: [component: ComponentNode]
  removeComponent: [component: ComponentNode]
  selectComponent: [component: ComponentNode]
  renameComponent: [component: ComponentNode, newName: string]
}>()
const themeVars = useThemeVars()

const isRemovable = computed(() => props.removable && props.component?.type !== ComponentTypes.GridItem)
const hasChildren = computed(() => props.component?.children?.length)

const displayTitle = computed(() => {
  if (!props.component) return props.title || getComponentTitle(props.type, t)

  const displayName = getNodeDisplayNameByComponentType(props.component)
  if (displayName && displayName !== props.component.type) {
    return displayName
  }
  return props.title || getComponentTitle(props.component.type, t)
})

const showComponentMenu = computed(() => {
  return props.showComponentManager && props.component
})

const menuOptions = computed<DropdownMixedOption[]>(() => {
  return [
    {
      label: t('canvasUI.canvasBuilder.reorder.rename'),
      key: 'rename',
      icon: () => h(CIcon, { icon: 'pen', color: themeVars.value.primary }),
      props: {
        style: {
          minWidth: '120px',
        },
      },
    },
    {
      label: t('canvasUI.canvasBuilder.reorder.delete'),
      key: 'delete',
      icon: () => h(CIcon, { icon: 'trash', color: themeVars.value.error }),
      props: {
        style: {
          minWidth: '120px',
          color: themeVars.value.error,
        },
      },
    },
  ]
})

const handleMenuSelect = (key: string) => {
  if (!props.component) return

  if (key === 'delete') {
    emit('removeComponent', props.component)
  } else if (key === 'rename') {
    handleRename()
  }
}

const showRenameModal = ref(false)
const renameForm = ref('')

const getCurrentComponentName = () => {
  return displayTitle.value
}

const handleRename = () => {
  if (!props.component) return

  const currentName = getCurrentComponentName()
  renameForm.value = currentName
  showRenameModal.value = true
}

const onRenameSubmit = (newName: string) => {
  if (!props.component) return

  const currentName = getCurrentComponentName()

  if (newName.trim() !== currentName) {
    emit('renameComponent', props.component, newName.trim())
  }
  showRenameModal.value = false
}

const icon = props.icon ?? {
  name: ComponentIcon[props.type as keyof typeof ComponentIcon],
  type: componentIconType[props.type as keyof typeof ComponentIcon],
}
</script>

<style scoped lang="scss">
.c-component-list-item__drag-handle {
  font-weight: 900;
}

.title {
  line-height: normal;
  letter-spacing: 0.25px;
  color: v-bind('themeVars.text');
}

.description {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: v-bind('themeVars.text2');
}
</style>
